import { createClient, type SupabaseClient } from "@supabase/supabase-js";

export const brandRegistrationPath = "/brand/register";
export const brandRegistrationUrl = "https://app.uncreator.club/brand/register";

let brandRegistrationCallbackFailed = false;

/** Consumes allowed Supabase credentials before the dashboard or analytics loads. */
export const consumeBrandRegistrationCallback = async (
  environment: Pick<
    ImportMetaEnv,
    "VITE_SUPABASE_URL" | "VITE_SUPABASE_PUBLISHABLE_KEY"
  >,
  location = window.location,
  history = window.history,
) => {
  if (
    location.pathname !== brandRegistrationPath ||
    (!location.search && !location.hash)
  )
    return;
  brandRegistrationCallbackFailed = false;
  const query = new URLSearchParams(location.search);
  const fragment = new URLSearchParams(location.hash.replace(/^#/, ""));
  const hasCode =
    query.has("code") &&
    !fragment.size &&
    [...query.keys()].every((key) => key === "code");
  const allowedFragment = new Set([
    "access_token",
    "refresh_token",
    "expires_in",
    "expires_at",
    "token_type",
    "type",
    "provider_token",
    "provider_refresh_token",
  ]);
  const hasTokens =
    !query.size &&
    fragment.has("access_token") &&
    fragment.has("refresh_token") &&
    (fragment.get("type") === "magiclink" ||
      fragment.get("type") === "signup") &&
    [...fragment.keys()].every((key) => allowedFragment.has(key));
  try {
    if (
      !environment.VITE_SUPABASE_URL ||
      !environment.VITE_SUPABASE_PUBLISHABLE_KEY ||
      (!hasCode && !hasTokens)
    )
      throw new Error("Invalid Brand callback.");
    const client = createClient(
      environment.VITE_SUPABASE_URL,
      environment.VITE_SUPABASE_PUBLISHABLE_KEY,
      { auth: { detectSessionInUrl: false } },
    );
    const result = hasCode
      ? await client.auth.exchangeCodeForSession(query.get("code")!)
      : await client.auth.setSession({
          access_token: fragment.get("access_token")!,
          refresh_token: fragment.get("refresh_token")!,
        });
    if (result.error) throw result.error;
  } catch {
    brandRegistrationCallbackFailed = true;
  } finally {
    // Do not leave one-use credentials in URL or browser history.
    history.replaceState(null, "", brandRegistrationPath);
  }
};

export const hasBrandRegistrationCallbackFailure = () =>
  brandRegistrationCallbackFailed;

export type DashboardAuth = {
  beginTotpEnrollment?: () => Promise<TotpEnrollmentMaterial>;
  cancelTotpEnrollment?: (factorId: string) => Promise<void>;
  clearLocalSession?: () => Promise<void>;
  getAccessToken: () => Promise<string | undefined>;
  getCurrentEmail?: () => Promise<string | undefined>;
  getCurrentSessionIdentity?: () => Promise<
    DashboardSessionIdentity | undefined
  >;
  hasVerifiedTotp?: () => Promise<boolean>;
  reconcileTotp?: () => Promise<TotpReconciliation>;
  onAuthChange?: (listener: (event: DashboardAuthEvent) => void) => () => void;
  sendMagicLink: (
    email: string,
    returnPath?: typeof brandRegistrationPath,
  ) => Promise<void>;
  sendGlobalLogoutProof?: (email: string) => Promise<void>;
  signOutCurrentBrowser: () => Promise<void>;
  verifyGlobalLogoutEmailCode?: (email: string, code: string) => Promise<void>;
  verifyGlobalLogoutTotp?: (code: string) => Promise<void>;
  verifyTotpEnrollment?: (factorId: string, code: string) => Promise<void>;
  verifyTotpFactor?: (factorId: string, code: string) => Promise<void>;
};

/** Enrollment material is returned only to the currently mounted component.
 * Never put it in URL, storage, telemetry, errors, fixtures, or route data. */
export type TotpEnrollmentMaterial = {
  factorId: string;
  qrCode?: string;
  uri?: string;
  secret?: string;
};
export type TotpReconciliation = {
  assurance: "aal1" | "aal2" | "unknown";
  hasVerifiedFactor: boolean;
  verifiedFactorIds: string[];
  unverifiedFactorIds: string[];
};

export type DashboardAuthEvent = {
  name:
    | "INITIAL_SESSION"
    | "PASSWORD_RECOVERY"
    | "SIGNED_IN"
    | "SIGNED_OUT"
    | "TOKEN_REFRESHED"
    | "USER_UPDATED"
    | "MFA_CHALLENGE_VERIFIED";
  hasSession: boolean;
  /** True only while handling a Supabase email-link/OTP callback URL. */
  isProofReturn: boolean;
};

export type DashboardSessionIdentity = {
  sessionId: string;
  subjectId: string;
};

export const captureCallbackProofEvidence = (location = window.location) => {
  const query = new URLSearchParams(location.search);
  const hash = new URLSearchParams(location.hash.replace(/^#/, ""));
  return (
    query.has("code") ||
    query.has("token_hash") ||
    hash.has("access_token") ||
    hash.has("token_hash")
  );
};

const sessionIdFromAccessToken = (accessToken: string) => {
  const encodedPayload = accessToken.split(".")[1];
  if (!encodedPayload) return undefined;
  try {
    const base64Payload = encodedPayload.replace(/-/g, "+").replace(/_/g, "/");
    const padding = "=".repeat((4 - (base64Payload.length % 4)) % 4);
    const payload = JSON.parse(
      decodeURIComponent(
        atob(`${base64Payload}${padding}`)
          .split("")
          .map(
            (character) =>
              `%${character.charCodeAt(0).toString(16).padStart(2, "0")}`,
          )
          .join(""),
      ),
    ) as { session_id?: unknown };
    return typeof payload.session_id === "string" && payload.session_id
      ? payload.session_id
      : undefined;
  } catch {
    return undefined;
  }
};

export const createDashboardAuth = (
  environment: ImportMetaEnv,
  client?: SupabaseClient,
  initialCallbackProofEvidence = captureCallbackProofEvidence(),
): DashboardAuth => {
  // Supabase may clean a callback URL while its client initializes. Keep only
  // this boolean (never the URL parameters themselves) until the initial or
  // signed-in event that reports the callback-created session.
  let callbackProofEvidence = initialCallbackProofEvidence;
  let pendingMagicLink: { email: string; request: Promise<void> } | undefined;
  const supabase =
    client ??
    (environment.VITE_SUPABASE_URL && environment.VITE_SUPABASE_PUBLISHABLE_KEY
      ? createClient(
          environment.VITE_SUPABASE_URL,
          environment.VITE_SUPABASE_PUBLISHABLE_KEY,
        )
      : undefined);
  const requireSupabase = () => {
    if (!supabase) throw new Error("Sign-in is not configured yet.");
    return supabase;
  };
  const getSession = async () => {
    if (!supabase) return undefined;
    const { data, error } = await supabase.auth.getSession();
    if (error) throw error;
    return data.session;
  };
  const signOutLocal = async () => {
    const { error } = await requireSupabase().auth.signOut({ scope: "local" });
    if (error) throw error;
  };
  const readTotpReconciliation = async (client: SupabaseClient) => {
    const [factors, assurance] = await Promise.all([
      client.auth.mfa.listFactors(),
      client.auth.mfa.getAuthenticatorAssuranceLevel(),
    ]);
    if (factors.error) throw factors.error;
    if (assurance.error) throw assurance.error;
    const totpFactors = factors.data.all.filter(
      (factor) => factor.factor_type === "totp",
    );
    return {
      assurance:
        assurance.data.currentLevel === "aal2"
          ? ("aal2" as const)
          : assurance.data.currentLevel === "aal1"
            ? ("aal1" as const)
            : ("unknown" as const),
      hasVerifiedFactor: totpFactors.some(
        (factor) => factor.status === "verified",
      ),
      verifiedFactorIds: totpFactors
        .filter((factor) => factor.status === "verified")
        .map((factor) => factor.id),
      unverifiedFactorIds: totpFactors
        .filter((factor) => factor.status === "unverified")
        .map((factor) => factor.id),
    };
  };
  const challengeAndVerifyTotp = async (
    client: SupabaseClient,
    factorId: string,
    code: string,
  ) => {
    const challenge = await client.auth.mfa.challenge({ factorId });
    if (challenge.error) throw challenge.error;
    const verification = await client.auth.mfa.verify({
      factorId,
      challengeId: challenge.data.id,
      code,
    });
    if (verification.error) throw verification.error;
    const refreshed = await client.auth.refreshSession();
    if (refreshed.error) throw refreshed.error;
    const current = await readTotpReconciliation(client);
    if (!current.hasVerifiedFactor || current.assurance !== "aal2")
      throw new Error("Authenticator assurance could not be confirmed.");
  };

  return {
    async clearLocalSession() {
      if (!supabase) return;
      await signOutLocal();
    },
    onAuthChange(listener) {
      if (!supabase) return () => undefined;
      const { data } = supabase.auth.onAuthStateChange((name, session) => {
        const isProofReturn =
          (name === "SIGNED_IN" || name === "INITIAL_SESSION") &&
          (callbackProofEvidence || captureCallbackProofEvidence());
        if (isProofReturn) callbackProofEvidence = false;
        listener({
          name,
          hasSession: Boolean(session),
          isProofReturn,
        });
      });
      return () => data.subscription.unsubscribe();
    },
    async getAccessToken() {
      return (await getSession())?.access_token;
    },
    async getCurrentEmail() {
      return (await getSession())?.user.email;
    },
    async getCurrentSessionIdentity() {
      const session = await getSession();
      if (!session?.user.id) return undefined;
      const sessionId = sessionIdFromAccessToken(session.access_token);
      return sessionId ? { subjectId: session.user.id, sessionId } : undefined;
    },
    async hasVerifiedTotp() {
      if (!supabase) return false;
      const { data, error } = await supabase.auth.mfa.listFactors();
      if (error) throw error;
      return data.totp.some((factor) => factor.status === "verified");
    },
    async reconcileTotp() {
      if (!supabase)
        return {
          assurance: "unknown",
          hasVerifiedFactor: false,
          verifiedFactorIds: [],
          unverifiedFactorIds: [],
        };
      return readTotpReconciliation(supabase);
    },
    async beginTotpEnrollment() {
      const { data, error } = await requireSupabase().auth.mfa.enroll({
        factorType: "totp",
      });
      if (error) throw error;
      return {
        factorId: data.id,
        qrCode: data.totp.qr_code,
        secret: data.totp.secret,
        uri: data.totp.uri,
      };
    },
    async verifyTotpEnrollment(factorId, code) {
      await challengeAndVerifyTotp(requireSupabase(), factorId, code);
    },
    async verifyTotpFactor(factorId, code) {
      const client = requireSupabase();
      const current = await readTotpReconciliation(client);
      if (!current.verifiedFactorIds.includes(factorId))
        throw new Error("Only an existing verified authenticator can be used.");
      await challengeAndVerifyTotp(client, factorId, code);
    },
    async cancelTotpEnrollment(factorId) {
      const client = requireSupabase();
      const factors = await client.auth.mfa.listFactors();
      if (factors.error) throw factors.error;
      const factor = factors.data.all.find((item) => item.id === factorId);
      if (!factor || factor.status !== "unverified")
        throw new Error(
          "Only an in-progress authenticator enrollment can be removed.",
        );
      const result = await client.auth.mfa.unenroll({ factorId });
      if (result.error) throw result.error;
    },
    async sendMagicLink(email, returnPath) {
      const normalizedEmail = email.trim().toLowerCase();
      if (pendingMagicLink) {
        if (pendingMagicLink.email === normalizedEmail)
          return pendingMagicLink.request;
        throw new Error("Another sign-in link request is already pending.");
      }
      const request = (async () => {
        const { error } = await requireSupabase().auth.signInWithOtp({
          email: normalizedEmail,
          options: {
            emailRedirectTo:
              returnPath === brandRegistrationPath
                ? brandRegistrationUrl
                : window.location.origin,
          },
        });
        if (error) throw error;
      })();
      pendingMagicLink = { email: normalizedEmail, request };
      try {
        await request;
      } finally {
        pendingMagicLink = undefined;
      }
    },
    async sendGlobalLogoutProof(email) {
      const { error } = await requireSupabase().auth.signInWithOtp({
        email,
        options: {
          shouldCreateUser: false,
          emailRedirectTo: `${window.location.origin}/account/security`,
        },
      });
      if (error) throw error;
    },
    async signOutCurrentBrowser() {
      await signOutLocal();
    },
    async verifyGlobalLogoutEmailCode(email, code) {
      const { error } = await requireSupabase().auth.verifyOtp({
        email,
        token: code,
        type: "email",
      });
      if (error) throw error;
    },
    async verifyGlobalLogoutTotp(code) {
      const client = requireSupabase();
      const { data, error } = await client.auth.mfa.listFactors();
      if (error) throw error;
      const factor = data.totp.find((item) => item.status === "verified");
      if (!factor) throw new Error("An authenticator code is not configured.");
      const challenge = await client.auth.mfa.challenge({
        factorId: factor.id,
      });
      if (challenge.error) throw challenge.error;
      const verification = await client.auth.mfa.verify({
        factorId: factor.id,
        challengeId: challenge.data.id,
        code,
      });
      if (verification.error) throw verification.error;
    },
  };
};
